import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  Input,
  signal,
} from '@angular/core';
import {
  asapScheduler,
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  from,
  map,
  observeOn,
  of,
  shareReplay,
  startWith,
  switchMap,
  tap,
} from 'rxjs';

import { Maybe, Nullish } from '@plugins/shared/types';

import { UserDetailsService } from '../../../public/services';
import { USERDETAILS_DETAILED_TITLE } from '../../../public/tokens';
import type {
  AllEntityTypesWithFails,
  RequestStatus,
  ValidSearchTypes,
} from '../../utils/types.private';

/** Specifies available inputs for `UserNameComponent`, `UserAvatarComponent`,
 *  and `UserEntityComponent`.
 *
 * Also handles entity fetching on input changes. */

@Component({
  selector: 'plugins-userdetails-abstract-atomic',
  standalone: false,
  template: '',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export abstract class AtomicUserComponent {
  /**  The user identifier is used to fetch data from the `UserDetailsService` */
  // Keeping deprecated Inputuntil RxJs interop is stable.
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() set userId(val: string) {
    this._userId.next(val);
  }
  /** For userProvider, an entity type is expected. However,
   * the `{id: string, type: "error"}` object
   * can be forwarded in cases of request errors. */
  // Keeping deprecated Inputuntil RxJs interop is stable.
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() set userProvider(val: AllEntityTypesWithFails | undefined) {
    this._userProvider.next(val ?? null);
  }
  /** Set the size of the component. Styles are embedded into the host element.
    - A number will be interpreted as having a pixel unit.
    - A string will be checked whether it contains any of the supported units. If a stringified number or an invalid unit is passed, it defaults to pixels.
    - Currently supported units: `px, rem, em, vw, vh, vmin, vmax, %`.
    - If no size property is provided, component defaults to inherited font-size. */
  readonly size = input<Maybe<string | number>>('');
  /** Determine whether the component should have a title prop. Defaults to `true`.
   * Note that title does not display for request error placeholders */
  readonly showTitle = input<boolean>(true);
  /** Determines if the title prop should display entity details
   * (type and user count where applicable). Defaults to `false`.
   *
   * Detailed title can also be set via the `USERDETAILS_DETAILED_TITLE` injection token.
   *
   * Has no effect if showTitle is falsy.
   *
   * Example:
   * ```
   * John Doe - User
   * Normal Users - Group (2.35K members)
   * ```
   * Defaults to `false` */
  readonly detailedTitle = input<boolean, boolean | Nullish>(false, {
    transform: (val: boolean | Nullish) => val ?? false,
  });
  /** Can be used for fetch optimization or narrowing when making calls to the `UserDetailsService`.
   *
   * Defaults to `all`. */
  readonly searchType = input<ValidSearchTypes>('all');
  /** Should component link to user profile page. Defaults to `true` */
  readonly profileLinkEnabled = input(true);

  // DI section
  protected readonly userDetailsService = inject(UserDetailsService);
  protected readonly detailedTitleToken = inject(USERDETAILS_DETAILED_TITLE, {
    optional: true,
  });

  protected _userId = new BehaviorSubject<string | null>(null);
  protected _userProvider = new BehaviorSubject<AllEntityTypesWithFails | null>(
    null,
  );

  protected readonly status = signal<RequestStatus>('pending');
  /** Exposed current request status information for the target entity.  */
  readonly entityStatus = computed(() => this.status());
  protected readonly userSignal = signal<AllEntityTypesWithFails | undefined>(
    undefined,
  );
  /** Exposed entity data object */
  readonly user = computed(() => this.userSignal());
  /** Exposed entity id */
  readonly dataUserId = signal<string | Nullish>(undefined);

  /** Refetch on truthy userId and userProvider changes.
   *
   * After RxJS interop enters stable, we can also refetch
   * on searchType change */
  user$ = combineLatest({
    userId: this._userId.asObservable(),
    userProvider: this._userProvider.asObservable(),
  }).pipe(
    distinctUntilChanged(
      (prev, curr) =>
        prev.userId === curr.userId &&
        JSON.stringify(prev.userProvider) === JSON.stringify(curr.userProvider),
    ),
    tap(({ userId, userProvider }) => {
      this.status.set('pending');
      this.userSignal.set(undefined);
      this.dataUserId.set(userProvider?.id ?? userId);
    }),
    filter((args) => !!args.userId || !!args.userProvider),
    // Asap scheduler needed so that searchType assignment via Input can be registered
    observeOn(asapScheduler),
    switchMap(({ userId, userProvider }) => {
      if (userProvider) {
        return of(userProvider);
      }
      return from(
        // We can assert because we know the filter in the previous step ensures
        // that at least one value is non nullish
        this.userDetailsService.findEntity(userId as string, this.searchType()),
      ).pipe(map((entityArr) => entityArr[0]));
    }),
    filter((user) => !!user),
    tap((user) => {
      this.status.set(user.type !== 'error' ? 'success' : 'error');
      this.userSignal.set(user);
    }),
    startWith(undefined),
    shareReplay(1),
  );
}
